import React from 'react';
import {
  Text,
  Button,
  Tooltip,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowLeft16Regular,
  CalendarLock16Regular,
  CheckmarkCircle16Regular,
} from '@fluentui/react-icons';
import PhaseTimelineStrip from './PhaseTimelineStrip';
import { TimelineSegmentInput } from '../lib/phaseTimeline';

// The schedule panel = the phase timeline overview + its stage-aware controls,
// combined into one bordered surface so the controls and the strip reserve their
// footprint together (adding the first phase never reflows the grid below).

export type ScheduleSaveStatus = 'saving' | 'unsaved' | 'error' | 'saved';

interface PhaseSchedulePanelProps {
  segments: TimelineSegmentInput[];
  stage: 'planning' | 'provisioning';
  saveStatus: ScheduleSaveStatus;
  saveError?: string;
  hasChanges: boolean;
  /** Every phase has both dates — gates the Lock CTA. */
  canLock: boolean;
  /** Whether the project has any phases yet — hides the save status on a fresh
   *  project where there's nothing to save. */
  hasPhases: boolean;
  defaultBin?: { label: string } | null;
  onLock: () => void;
  onEditSchedule: () => void;
  onDiscard: () => void;
  onRetrySave: () => void;
  /** Apply a boundary drag/keyboard edit from the timeline strip. Only wired up
   *  in the planning stage; provisioning keeps the strip read-only. */
  onBoundaryEdit?: (phaseId: string, edge: 'start' | 'end', date: string) => void;
}

const useStyles = makeStyles({
  // One bordered surface: control row, divider, then timeline.
  panel: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '10px',
    ...shorthands.padding('10px', '12px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('6px'),
  },
  controlRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: '16px',
    flexWrap: 'wrap',
    rowGap: '8px',
  },
  leftCluster: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '12px',
    flexWrap: 'wrap',
    rowGap: '4px',
  },
  rightCluster: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
  },
  title: {
    fontWeight: tokens.fontWeightSemibold,
  },
  defaultBinChip: {
    color: tokens.colorNeutralForeground3,
  },
  divider: {
    height: '1px',
    backgroundColor: tokens.colorNeutralStroke2,
  },
  // Empty-state hint shown in place of the strip until something's dated. Holds
  // the strip's height so the panel footprint is constant.
  hint: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '52px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
    fontSize: tokens.fontSizeBase200,
  },
  savePill: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: '6px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  savePillSaved: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: '6px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteGreenForeground1,
  },
  savePillError: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: '4px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
});

const PhaseSchedulePanel: React.FC<PhaseSchedulePanelProps> = ({
  segments,
  stage,
  saveStatus,
  saveError,
  hasChanges,
  canLock,
  hasPhases,
  defaultBin,
  onLock,
  onEditSchedule,
  onDiscard,
  onRetrySave,
  onBoundaryEdit,
}) => {
  const styles = useStyles();

  const anyDated = segments.some((s) => !!s.startDate && !!s.endDate);

  // Nothing to save on a fresh project — keep the pill out until phases exist.
  const statusPill = !hasPhases ? null : saveStatus === 'saving' ||
    saveStatus === 'unsaved' ? (
      <span className={styles.savePill}>
        <Spinner size="tiny" />
        Saving…
      </span>
    ) : saveStatus === 'error' ? (
      <span className={styles.savePillError}>
        {saveError || 'Save failed'}
        <Button appearance="transparent" size="small" onClick={onRetrySave}>
          Retry
        </Button>
      </span>
    ) : (
      <span className={styles.savePillSaved}>
        <CheckmarkCircle16Regular />
        All changes saved
      </span>
    );

  const leftCluster =
    stage === 'planning' ? (
      <div className={styles.leftCluster}>
        <Text className={styles.title}>Schedule</Text>
      </div>
    ) : (
      <div className={styles.leftCluster}>
        <Button appearance="subtle" icon={<ArrowLeft16Regular />} onClick={onEditSchedule}>
          Edit schedule
        </Button>
        {defaultBin && (
          <Text size={200} className={styles.defaultBinChip}>
            Default bin: <strong>{defaultBin.label}</strong>
          </Text>
        )}
      </div>
    );

  const rightCluster =
    stage === 'planning' ? (
      <div className={styles.rightCluster}>
        {statusPill}
        {hasChanges && (
          <Button appearance="subtle" size="small" onClick={onDiscard}>
            Discard
          </Button>
        )}
        <Tooltip
          content={
            canLock
              ? 'Lock the schedule and start adding bins'
              : 'Give every phase a delivery and pickup date first'
          }
          relationship="label"
        >
          <Button
            appearance="primary"
            icon={<CalendarLock16Regular />}
            disabled={!canLock}
            onClick={onLock}
          >
            Lock schedule &amp; add bins
          </Button>
        </Tooltip>
      </div>
    ) : (
      <div className={styles.rightCluster}>{statusPill}</div>
    );

  return (
    <div className={styles.panel}>
      <div className={styles.controlRow}>
        {leftCluster}
        {rightCluster}
      </div>
      <div className={styles.divider} />
      {anyDated ? (
        <PhaseTimelineStrip
          segments={segments}
          // Drag/keyboard editing of the timeline is parked for now (possible v2).
          // The strip is a read-only overview; phase dates are edited on the cards
          // below. To re-enable, restore:
          //   onBoundaryEdit={stage === 'planning' ? onBoundaryEdit : undefined}
          onBoundaryEdit={undefined}
        />
      ) : (
        <div className={styles.hint}>Set each phase&rsquo;s dates to build the timeline.</div>
      )}
    </div>
  );
};

export default PhaseSchedulePanel;
